import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import {
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  Trophy,
  Target,
  BookOpen,
  Lightbulb,
} from "lucide-react";

// Quiz question types and interfaces
export interface QuizQuestion {
  id: string;
  type:
    | "multiple-choice"
    | "true-false"
    | "multiple-selection"
    | "matching"
    | "fill-blank"
    | "scenario";
  question: string;
  scenario?: string; // For scenario-based questions
  options?: string[]; // For multiple choice and selection
  matches?: { left: string; right: string }[]; // For matching questions
  correctAnswer: string | string[] | { [key: string]: string };
  explanation: string;
  points: number;
  difficulty: "beginner" | "intermediate" | "advanced";
  category?: string;
  hints?: string[];
  timeLimit?: number; // in seconds
  metadata?: {
    lessonId?: string;
    learningObjective?: string;
    prerequisites?: string[];
  };
}

export interface QuizResult {
  questionId: string;
  userAnswer: string | string[] | { [key: string]: string };
  correctAnswer: string | string[] | { [key: string]: string };
  isCorrect: boolean;
  points: number;
  timeSpent: number;
  hintsUsed: number;
}

export interface QuizConfig {
  id: string;
  title: string;
  description: string;
  questions: QuizQuestion[];
  timeLimit?: number; // Overall time limit in minutes
  passingScore: number; // Percentage
  allowRetries: boolean;
  showCorrectAnswers: boolean;
  randomizeQuestions: boolean;
  randomizeOptions: boolean;
  difficulty: "beginner" | "intermediate" | "advanced";
  category: string;
}

interface QuizEngineProps {
  config: QuizConfig;
  onComplete: (results: QuizResult[], score: number, passed: boolean) => void;
  onProgress?: (
    currentQuestion: number,
    totalQuestions: number,
    score: number,
  ) => void;
  className?: string;
}

export const QuizEngine: React.FC<QuizEngineProps> = ({
  config,
  onComplete,
  onProgress,
  className = "",
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Map<string, any>>(new Map());
  const [quizResults, setQuizResults] = useState<QuizResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(
    config.timeLimit ? config.timeLimit * 60 : null,
  );
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [hintsUsed, setHintsUsed] = useState<Map<string, number>>(new Map());
  const [showHint, setShowHint] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);

  // Process questions (randomize if needed)
  const processedQuestions = useMemo(() => {
    let questions = [...config.questions];

    if (config.randomizeQuestions) {
      questions = questions.sort(() => Math.random() - 0.5);
    }

    if (config.randomizeOptions) {
      questions = questions.map((q) => {
        if (
          q.options &&
          (q.type === "multiple-choice" || q.type === "multiple-selection")
        ) {
          return {
            ...q,
            options: [...q.options].sort(() => Math.random() - 0.5),
          };
        }
        return q;
      });
    }

    return questions;
  }, [config.questions, config.randomizeQuestions, config.randomizeOptions]);

  const currentQuestion = processedQuestions[currentQuestionIndex];
  const totalQuestions = processedQuestions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  // Timer effect
  useEffect(() => {
    if (timeRemaining === null || showResults) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev !== null && prev <= 1) {
          handleQuizComplete();
          return 0;
        }
        return prev !== null ? prev - 1 : null;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, showResults]);

  // Update progress callback
  useEffect(() => {
    const currentScore = calculateCurrentScore();
    onProgress?.(currentQuestionIndex + 1, totalQuestions, currentScore);
  }, [currentQuestionIndex, userAnswers, onProgress]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateCurrentScore = (): number => {
    let totalPoints = 0;
    let earnedPoints = 0;

    processedQuestions.forEach((question, index) => {
      totalPoints += question.points;
      const userAnswer = userAnswers.get(question.id);
      if (userAnswer && isAnswerCorrect(question, userAnswer)) {
        earnedPoints += question.points;
      }
    });

    return totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
  };

  const isAnswerCorrect = (question: QuizQuestion, answer: any): boolean => {
    switch (question.type) {
      case "multiple-choice":
      case "true-false":
      case "fill-blank":
        return answer === question.correctAnswer;

      case "multiple-selection":
        const userSelection = Array.isArray(answer) ? answer : [];
        const correctSelection = Array.isArray(question.correctAnswer)
          ? question.correctAnswer
          : [];
        return (
          userSelection.length === correctSelection.length &&
          userSelection.every((ans) => correctSelection.includes(ans))
        );

      case "matching":
        if (
          typeof answer !== "object" ||
          typeof question.correctAnswer !== "object"
        ) {
          return false;
        }
        return Object.entries(answer).every(
          ([key, value]) =>
            (question.correctAnswer as Record<string, string>)[key] === value,
        );

      default:
        return false;
    }
  };

  const handleAnswerChange = (answer: any) => {
    setUserAnswers((prev) => new Map(prev.set(currentQuestion.id, answer)));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      saveQuestionResult(timeSpent);
      setCurrentQuestionIndex((prev) => prev + 1);
      setQuestionStartTime(Date.now());
      setShowHint(false);
    } else {
      handleQuizComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setShowHint(false);
    }
  };

  const handleQuizComplete = () => {
    const finalResults: QuizResult[] = processedQuestions.map((question) => {
      const userAnswer = userAnswers.get(question.id);
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      const hints = hintsUsed.get(question.id) || 0;

      return {
        questionId: question.id,
        userAnswer: userAnswer || "",
        correctAnswer: question.correctAnswer,
        isCorrect: userAnswer ? isAnswerCorrect(question, userAnswer) : false,
        points:
          userAnswer && isAnswerCorrect(question, userAnswer)
            ? question.points
            : 0,
        timeSpent,
        hintsUsed: hints,
      };
    });

    const totalPossiblePoints = processedQuestions.reduce(
      (sum, q) => sum + q.points,
      0,
    );
    const earnedPoints = finalResults.reduce((sum, r) => sum + r.points, 0);
    const finalScore =
      totalPossiblePoints > 0
        ? Math.round((earnedPoints / totalPossiblePoints) * 100)
        : 0;
    const passed = finalScore >= config.passingScore;

    setQuizResults(finalResults);
    setShowResults(true);
    onComplete(finalResults, finalScore, passed);
  };

  const saveQuestionResult = (timeSpent: number) => {
    // This would typically save to a service
    // For now, we'll just track it in memory
  };

  const handleHint = () => {
    if (currentQuestion.hints && currentQuestion.hints.length > 0) {
      const currentHints = hintsUsed.get(currentQuestion.id) || 0;
      if (currentHints < currentQuestion.hints.length) {
        setHintsUsed(
          (prev) => new Map(prev.set(currentQuestion.id, currentHints + 1)),
        );
        setShowHint(true);
      }
    }
  };

  const handleRetry = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers(new Map());
    setQuizResults([]);
    setShowResults(false);
    setTimeRemaining(config.timeLimit ? config.timeLimit * 60 : null);
    setQuestionStartTime(Date.now());
    setHintsUsed(new Map());
    setShowHint(false);
    setReviewMode(false);
  };

  const currentHint =
    currentQuestion.hints?.[hintsUsed.get(currentQuestion.id) || 0];

  if (showResults) {
    return (
      <QuizResults
        results={quizResults}
        config={config}
        score={calculateCurrentScore()}
        onRetry={config.allowRetries ? handleRetry : undefined}
        onReview={() => setReviewMode(true)}
        className={className}
      />
    );
  }

  if (reviewMode) {
    return (
      <QuizReview
        questions={processedQuestions}
        results={quizResults}
        onBack={() => setReviewMode(false)}
        className={className}
      />
    );
  }

  return (
    <div className={`max-w-4xl mx-auto space-y-6 ${className}`}>
      {/* Quiz Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-2xl">{config.title}</CardTitle>
              <CardDescription className="mt-2">
                {config.description}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{config.difficulty}</Badge>
              <Badge variant="secondary">{config.category}</Badge>
              {timeRemaining !== null && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                    timeRemaining < 60
                      ? "bg-red-100 text-red-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  {formatTime(timeRemaining)}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </span>
                <span>Score: {calculateCurrentScore()}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>

            {/* Question Navigation */}
            <div className="flex flex-wrap gap-2">
              {processedQuestions.map((question, index) => {
                const hasAnswer = userAnswers.has(question.id);
                const isCurrent = index === currentQuestionIndex;

                return (
                  <button
                    key={question.id}
                    onClick={() => setCurrentQuestionIndex(index)}
                    className={`w-10 h-10 rounded-lg border-2 text-sm font-medium transition-colors ${
                      isCurrent
                        ? "border-blue-500 bg-blue-500 text-white"
                        : hasAnswer
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Question */}
      <QuestionRenderer
        question={currentQuestion}
        answer={userAnswers.get(currentQuestion.id)}
        onAnswerChange={handleAnswerChange}
        showHint={showHint}
        hint={currentHint}
        onHint={handleHint}
        hintsRemaining={
          (currentQuestion.hints?.length || 0) -
          (hintsUsed.get(currentQuestion.id) || 0)
        }
      />

      {/* Navigation Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {currentQuestion.hints &&
                currentQuestion.hints.length > 0 &&
                !showHint && (
                  <Button
                    variant="outline"
                    onClick={handleHint}
                    disabled={
                      (hintsUsed.get(currentQuestion.id) || 0) >=
                      currentQuestion.hints.length
                    }
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Hint ({hintsUsed.get(currentQuestion.id) || 0}/
                    {currentQuestion.hints.length})
                  </Button>
                )}
            </div>

            <Button
              onClick={handleNext}
              disabled={!userAnswers.has(currentQuestion.id)}
            >
              {currentQuestionIndex === totalQuestions - 1
                ? "Complete Quiz"
                : "Next"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Question Renderer Component
interface QuestionRendererProps {
  question: QuizQuestion;
  answer: any;
  onAnswerChange: (answer: any) => void;
  showHint: boolean;
  hint?: string;
  onHint: () => void;
  hintsRemaining: number;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  answer,
  onAnswerChange,
  showHint,
  hint,
  onHint,
  hintsRemaining,
}) => {
  const renderQuestionInput = () => {
    switch (question.type) {
      case "multiple-choice":
        return (
          <div className="space-y-3">
            {question.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={answer === option}
                  onChange={(e) => onAnswerChange(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="flex-1">{option}</span>
              </label>
            ))}
          </div>
        );

      case "true-false":
        return (
          <div className="space-y-3">
            {["True", "False"].map((option) => (
              <label
                key={option}
                className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={answer === option}
                  onChange={(e) => onAnswerChange(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="flex-1">{option}</span>
              </label>
            ))}
          </div>
        );

      case "multiple-selection":
        const selectedOptions = Array.isArray(answer) ? answer : [];
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-2">Select all that apply:</p>
            {question.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedOptions.includes(option)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onAnswerChange([...selectedOptions, option]);
                    } else {
                      onAnswerChange(
                        selectedOptions.filter((opt) => opt !== option),
                      );
                    }
                  }}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="flex-1">{option}</span>
              </label>
            ))}
          </div>
        );

      case "matching":
        const matches = answer || {};
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Match each item from Column A with the correct item in Column B:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Column A</h4>
                {question.matches?.map((match, index) => (
                  <div key={index} className="p-2 border rounded mb-2">
                    {match.left}
                  </div>
                ))}
              </div>
              <div>
                <h4 className="font-medium mb-2">Column B</h4>
                {question.matches?.map((match, index) => (
                  <select
                    key={index}
                    value={matches[index] || ""}
                    onChange={(e) =>
                      onAnswerChange({ ...matches, [index]: e.target.value })
                    }
                    className="w-full p-2 border rounded mb-2"
                  >
                    <option value="">Select match...</option>
                    {question.matches?.map((m, i) => (
                      <option key={i} value={m.right}>
                        {m.right}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
          </div>
        );

      case "fill-blank":
        return (
          <div className="space-y-4">
            <p className="text-lg">Complete the statement:</p>
            <input
              type="text"
              value={answer || ""}
              onChange={(e) => onAnswerChange(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full p-3 border rounded-lg text-lg"
            />
          </div>
        );

      case "scenario":
        return (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Scenario:</h4>
              <p className="text-blue-700">{question.scenario}</p>
            </div>
            <div className="space-y-3">
              <p className="font-medium">{question.question}</p>
              {question.options?.map((option, index) => (
                <label
                  key={index}
                  className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={answer === option}
                    onChange={(e) => onAnswerChange(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="flex-1">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return <p>Unsupported question type: {question.type}</p>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{question.type.replace("-", " ")}</Badge>
              <Badge variant="secondary">{question.points} points</Badge>
              {question.difficulty && (
                <Badge variant="outline">{question.difficulty}</Badge>
              )}
            </div>
            <CardTitle className="text-xl">{question.question}</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {renderQuestionInput()}

          {/* Hint Section */}
          {showHint && hint && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-yellow-800 mb-1">Hint:</h4>
                  <p className="text-yellow-700">{hint}</p>
                </div>
              </div>
            </div>
          )}

          {/* Hint Button */}
          {!showHint && hintsRemaining > 0 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={onHint}
                className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Use Hint ({hintsRemaining} remaining)
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Quiz Results Component
interface QuizResultsProps {
  results: QuizResult[];
  config: QuizConfig;
  score: number;
  onRetry?: () => void;
  onReview?: () => void;
  className?: string;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  results,
  config,
  score,
  onRetry,
  onReview,
  className = "",
}) => {
  const passed = score >= config.passingScore;
  const totalPoints = results.reduce(
    (sum, r) => sum + (r.isCorrect ? r.points : 0),
    0,
  );
  const maxPoints = config.questions.reduce((sum, q) => sum + q.points, 0);
  const correctAnswers = results.filter((r) => r.isCorrect).length;
  const totalTime = results.reduce((sum, r) => sum + r.timeSpent, 0);

  return (
    <div className={`max-w-4xl mx-auto space-y-6 ${className}`}>
      {/* Results Header */}
      <Card
        className={
          passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
        }
      >
        <CardHeader>
          <div className="text-center">
            <div
              className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                passed ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {passed ? (
                <Trophy className="w-10 h-10 text-white" />
              ) : (
                <AlertCircle className="w-10 h-10 text-white" />
              )}
            </div>
            <CardTitle
              className={`text-2xl ${passed ? "text-green-800" : "text-red-800"}`}
            >
              {passed ? "Congratulations!" : "Quiz Completed"}
            </CardTitle>
            <CardDescription
              className={passed ? "text-green-700" : "text-red-700"}
            >
              {passed
                ? `You passed with a score of ${score}%!`
                : `You scored ${score}%. The passing score is ${config.passingScore}%.`}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{score}%</div>
              <div className="text-sm text-gray-600">Final Score</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {correctAnswers}/{results.length}
              </div>
              <div className="text-sm text-gray-600">Correct Answers</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {totalPoints}/{maxPoints}
              </div>
              <div className="text-sm text-gray-600">Points Earned</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {Math.floor(totalTime / 60)}m
              </div>
              <div className="text-sm text-gray-600">Time Spent</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Detailed Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {results.map((result, index) => (
              <div key={result.questionId} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Question {index + 1}</span>
                    {result.isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {result.points}/{config.questions[index]?.points || 0}{" "}
                    points
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Your answer: </span>
                    <span
                      className={
                        result.isCorrect ? "text-green-700" : "text-red-700"
                      }
                    >
                      {Array.isArray(result.userAnswer)
                        ? result.userAnswer.join(", ")
                        : typeof result.userAnswer === "object"
                          ? JSON.stringify(result.userAnswer)
                          : result.userAnswer}
                    </span>
                  </div>
                  {!result.isCorrect && (
                    <div>
                      <span className="font-medium">Correct answer: </span>
                      <span className="text-green-700">
                        {Array.isArray(result.correctAnswer)
                          ? result.correctAnswer.join(", ")
                          : typeof result.correctAnswer === "object"
                            ? JSON.stringify(result.correctAnswer)
                            : result.correctAnswer}
                      </span>
                    </div>
                  )}
                  <div className="text-gray-600 italic">
                    {config.questions[index]?.explanation}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center gap-4">
            {onReview && (
              <Button variant="outline" onClick={onReview}>
                <BookOpen className="w-4 h-4 mr-2" />
                Review Answers
              </Button>
            )}
            {onRetry && !passed && (
              <Button onClick={onRetry}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry Quiz
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Quiz Review Component
interface QuizReviewProps {
  questions: QuizQuestion[];
  results: QuizResult[];
  onBack: () => void;
  className?: string;
}

const QuizReview: React.FC<QuizReviewProps> = ({
  questions,
  results,
  onBack,
  className = "",
}) => {
  return (
    <div className={`max-w-4xl mx-auto space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Quiz Review
          </CardTitle>
          <CardDescription>
            Review your answers and learn from explanations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {questions.map((question, index) => {
              const result = results[index];
              return (
                <div key={question.id} className="border rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">
                          Question {index + 1}
                        </span>
                        <Badge variant="outline">
                          {question.type.replace("-", " ")}
                        </Badge>
                        {result.isCorrect ? (
                          <Badge
                            variant="default"
                            className="bg-green-100 text-green-800"
                          >
                            Correct
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Incorrect</Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-medium">
                        {question.question}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium text-red-700">
                          Your answer:
                        </span>
                        <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                          {Array.isArray(result.userAnswer)
                            ? result.userAnswer.join(", ")
                            : typeof result.userAnswer === "object"
                              ? JSON.stringify(result.userAnswer)
                              : result.userAnswer}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-green-700">
                          Correct answer:
                        </span>
                        <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded">
                          {Array.isArray(result.correctAnswer)
                            ? result.correctAnswer.join(", ")
                            : typeof result.correctAnswer === "object"
                              ? JSON.stringify(result.correctAnswer)
                              : result.correctAnswer}
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className="font-medium text-blue-700">
                        Explanation:
                      </span>
                      <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded">
                        {question.explanation}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Results
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
